import React, { useState, useCallback, useEffect } from 'react';
import {
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Checkbox,
  FormControlLabel
} from '@mui/material';

import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../hooks/useAuth';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import axios from 'axios';

interface BetFormData {
  dates: string[];
  counters: string[];
  number: string;
  bigAmount: number;
  smallAmount: number;
}

// Operator names mapping (frontend display names to backend names)
const operatorNameMapping: Record<string, string> = {
  'Magnum': 'Magnum 4D',
  'Damacai 4D': 'Damacai 4D',
  'Singapore 4D': 'Singapore 4D',
  'Sports Toto': 'Sports Toto',
  'Cash Sweep': 'Cash Sweep',
  'Sabah 88': 'Sabah 88',
  'STC 4D': 'STC 4D',
  'Grand Dragon Lotto': 'Grand Dragon',
  '9 Lotto': '9 Lotto'
};

const reverseOperatorMapping: Record<string, string> = Object.fromEntries(
  Object.entries(operatorNameMapping).map(([key, value]) => [value, key])
);

const betSchema = yup.object({
  dates: yup.array()
    .of(yup.string().required())
    .min(1, 'At least one date is required')
    .required('Dates are required'),
  counters: yup.array()
    .of(yup.string().required())
    .min(1, 'At least one counter is required')
    .required('Counters are required'),
  number: yup.string()
    .matches(/^\d{4}$/, 'Number must be exactly 4 digits')
    .required('Number is required'),
  bigAmount: yup.number()
    .min(0, 'Amount must be positive')
    .max(1000, 'Maximum bet amount is $1000')
    .required('Big amount is required'),
  smallAmount: yup.number()
    .min(0, 'Amount must be positive')
    .max(1000, 'Maximum bet amount is $1000')
    .required('Small amount is required')
});

const steps = ['Entry', 'Preview', 'Acknowledgement'];

const PlaceBet: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [betData, setBetData] = useState<BetFormData | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [selectedDates, setSelectedDates] = useState<string[]>([new Date().toISOString().split('T')[0]]);
  const [selectedCounters, setSelectedCounters] = useState<string[]>([]);
  const [availableOperators, setAvailableOperators] = useState<string[]>([]);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { control, handleSubmit, formState: { errors }, watch, reset, setValue } = useForm<BetFormData>({
    resolver: yupResolver(betSchema),
    defaultValues: {
      dates: [new Date().toISOString().split('T')[0]],
      counters: [],
      number: '',
      bigAmount: 0,
      smallAmount: 0
    }
  });

  // Fetch available operators for selected dates
  const fetchAvailableOperators = useCallback(async (dates: string[]) => {
    if (dates.length === 0) {
      setAvailableOperators([]);
      return;
    }

    try {
      // Get available operators for each date and find intersection
      const operatorSets = await Promise.all(
        dates.map(async (date) => {
          const response = await axios.get(`/operator/available?date=${date}`);
          const data = response.data;
          return new Set<string>(
            (data.availableOperators || []).map((op: any) => op.Name as string)
          );
        })
      );

      // Find operators available for ALL selected dates
      if (operatorSets.length > 0) {
        let intersection = operatorSets[0];
        for (let i = 1; i < operatorSets.length; i++) {
          const currentSet = operatorSets[i];
          intersection = new Set<string>(
            Array.from(intersection).filter((op: string) => currentSet.has(op))
          );
        }
        
        // Map backend operator names to frontend display names
        const mappedOperators = Array.from(intersection)
          .map((backendName: string) => reverseOperatorMapping[backendName] || backendName)
          .filter((op: string | undefined): op is string => !!op); // Remove any undefined mappings
        
        setAvailableOperators(mappedOperators);
      } else {
        setAvailableOperators([]);
      }
    } catch (error) {
      console.error('Error fetching available operators:', error);
      setAvailableOperators([]);
    }
  }, []);

  // Fetch available operators when dates change
  useEffect(() => {
    if (selectedDates.length > 0) {
      fetchAvailableOperators(selectedDates);
    } else {
      setAvailableOperators([]);
      setSelectedCounters([]);
      setValue('counters', []);
    }
  }, [selectedDates, fetchAvailableOperators, setValue]);

  // Clear selected counters that are no longer available
  useEffect(() => {
    if (availableOperators.length > 0) {
      const validCounters = selectedCounters.filter(counter => availableOperators.includes(counter));
      if (validCounters.length !== selectedCounters.length) {
        setSelectedCounters(validCounters);
        setValue('counters', validCounters);
      }
    }
  }, [availableOperators, selectedCounters, setValue]);

  // Fetch active draws to get valid GameId and DrawId
  const { data: gameDrawsData } = useQuery({
    queryKey: ['active-draws'],
    queryFn: async () => {
      const response = await axios.get('/draw/active');
      return response.data;
    },
    refetchInterval: 300000
  });

  const placeBetMutation = useMutation({
    mutationFn: async (data: BetFormData) => {
      // Get the first available game and draw for betting
      const firstGame = gameDrawsData?.[0];
      const firstDraw = firstGame?.draws?.[0];
      
      if (!firstGame || !firstDraw) {
        throw new Error('No active draws available for betting');
      }
      
      // Create individual bets for each date-counter combination using correct API format
      const bets = [];
      for (const date of data.dates) {
        for (const counter of data.counters) {
          // Map frontend display name to backend operator name
          const backendOperatorName = operatorNameMapping[counter] || counter;
          bets.push({
            OperatorName: backendOperatorName, // Use mapped operator name
            DrawDate: date, // Use date as DrawDate
            Number: data.number,
            BetType: 'Big',
            BigAmount: data.bigAmount,
            SmallAmount: data.smallAmount,
            FourAAmount: 0,
            AAmount: 0,
            AbcAmount: 0,
            FiveDAmount: 0,
            SixDAmount: 0
          });
        }
      }
      
      const response = await axios.post('/bet/place-multiple', { Bets: bets });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['user-history'] });
      setActiveStep(2);
    }
  });

  const watchedValues = watch();
  const baseAmount = (Number(watchedValues.bigAmount) || 0) + (Number(watchedValues.smallAmount) || 0);
  const totalCombinations = selectedDates.length * selectedCounters.length;
  const totalAmount = baseAmount * totalCombinations;
  const canAfford = user ? user.credits >= totalAmount : false;

  // Helper functions for multiple selections
  const handleDateAdd = (date: string) => {
    if (!selectedDates.includes(date)) {
      const newDates = [...selectedDates, date];
      setSelectedDates(newDates);
      setValue('dates', newDates);
      // Available operators will be updated via useEffect
    }
  };

  const handleDateRemove = (date: string) => {
    const newDates = selectedDates.filter(d => d !== date);
    setSelectedDates(newDates);
    setValue('dates', newDates);
  };

  const handleCounterToggle = (counter: string) => {
    const newCounters = selectedCounters.includes(counter)
      ? selectedCounters.filter(c => c !== counter)
      : [...selectedCounters, counter];
    setSelectedCounters(newCounters);
    setValue('counters', newCounters);
  };

  const handleNext = () => {
    if (activeStep === 0) {
      handleSubmit((data) => {
        setBetData(data);
        setActiveStep(1);
      })();
    } else if (activeStep === 1) {
      setConfirmDialog(true);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleConfirmBet = () => {
    if (betData) {
      placeBetMutation.mutate(betData);
      setConfirmDialog(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setBetData(null);
    setSelectedDates([new Date().toISOString().split('T')[0]]);
    setSelectedCounters([]);
    reset();
  };

  const getCounterColor = (counter: string) => {
    const colors: { [key: string]: string } = {
      'Magnum': '#e91e63',
      'Damacai 4D': '#9c27b0',
      'Singapore 4D': '#673ab7',
      'Sports Toto': '#3f51b5',
      'Cash Sweep': '#2196f3',
      'Sabah 88': '#03a9f4',
      'STC 4D': '#00bcd4',
      'Grand Dragon Lotto': '#009688',
      '9 Lotto': '#4caf50'
    };
    return colors[counter] || '#757575';
  };

  const renderEntryStep = () => (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Enter Bet Details
      </Typography>
      
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" gutterBottom>
            Select Dates
          </Typography>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Add Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              onChange={(e) => handleDateAdd(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedDates.map((date) => (
                <Chip
                  key={date}
                  label={new Date(date).toLocaleDateString()}
                  onDelete={() => handleDateRemove(date)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
            {errors.dates && (
              <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                {errors.dates.message}
              </Typography>
            )}
          </Box>
        </Grid>
        
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" gutterBottom>
            Select Counters
          </Typography>
          <Box sx={{ mb: 2 }}>
            {selectedDates.length === 0 ? (
              <Alert severity="info">
                Please select at least one date to see available operators.
              </Alert>
            ) : availableOperators.length === 0 ? (
              <Alert severity="warning">
                No operators are available for betting on the selected date(s). 
                Regular operators require a special draw on Monday/Tuesday/Thursday/Friday.
              </Alert>
            ) : (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  {availableOperators.length} operator(s) available for the selected date(s)
                </Alert>
                <Grid container spacing={2}>
                  {availableOperators.map((counter) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={counter}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedCounters.includes(counter)}
                            onChange={() => handleCounterToggle(counter)}
                            color="primary"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: getCounterColor(counter)
                              }}
                            />
                            {counter}
                          </Box>
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
            {errors.counters && (
              <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                {errors.counters.message}
              </Typography>
            )}
          </Box>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="number"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="4-Digit Number"
                placeholder="e.g., 1234"
                inputProps={{ maxLength: 4 }}
                error={!!errors.number}
                helperText={errors.number?.message}
              />
            )}
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">Available Credits:</Typography>
            <Chip
              label={`$${user?.credits?.toFixed(2) || '0.00'}`}
              color={canAfford ? 'success' : 'error'}
              size="small"
            />
          </Box>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="bigAmount"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Big Amount ($)"
                type="number"
                inputProps={{ min: 0, max: 1000, step: 0.01 }}
                error={!!errors.bigAmount}
                helperText={errors.bigAmount?.message}
              />
            )}
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="smallAmount"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Small Amount ($)"
                type="number"
                inputProps={{ min: 0, max: 1000, step: 0.01 }}
                error={!!errors.smallAmount}
                helperText={errors.smallAmount?.message}
              />
            )}
          />
        </Grid>
        
        <Grid size={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Bet Summary
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Big Amount:</Typography>
                <Typography>${(Number(watchedValues.bigAmount) || 0).toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Small Amount:</Typography>
                <Typography>${(Number(watchedValues.smallAmount) || 0).toFixed(2)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">Total Amount:</Typography>
                <Typography variant="h6" color={canAfford ? 'success.main' : 'error.main'}>
                  ${totalAmount.toFixed(2)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {!canAfford && totalAmount > 0 && (
          <Grid size={12}>
            <Alert severity="error">
              Insufficient credits. Please reduce the bet amount or contact support to add credits.
            </Alert>
          </Grid>
        )}
      </Grid>
    </Paper>
  );

  const renderPreviewStep = () => (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Confirm Your Bets
      </Typography>
      
      {betData && (
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">Selected Dates ({betData.dates.length})</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {betData.dates.map((date) => (
                    <Chip
                      key={date}
                      label={new Date(date).toLocaleDateString()}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">Selected Counters ({betData.counters.length})</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {betData.counters.map((counter) => (
                    <Chip
                      key={counter}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: getCounterColor(counter)
                            }}
                          />
                          {counter}
                        </Box>
                      }
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">Number</Typography>
                <Typography variant="h4" color="primary">{betData.number}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">Amount per Bet</Typography>
                <Typography variant="h6">
                  ${(Number(betData.bigAmount) + Number(betData.smallAmount)).toFixed(2)}
                </Typography>
              </Grid>
              <Grid size={12}>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Total Combinations:</strong> {totalCombinations} bets<br/>
                    <strong>Total Amount:</strong> ${totalAmount.toFixed(2)}
                  </Typography>
                </Alert>
              </Grid>
              <Grid size={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Big Bet:</Typography>
                  <Typography>${Number(betData.bigAmount).toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Small Bet:</Typography>
                  <Typography>${Number(betData.smallAmount).toFixed(2)}</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
      
      <Alert severity="info" sx={{ mt: 2 }}>
        Please review your bet details carefully. Once confirmed, this bet cannot be cancelled.
      </Alert>
    </Paper>
  );

  const renderAcknowledgementStep = () => (
    <Paper sx={{ p: 3, textAlign: 'center' }}>
      <Typography variant="h5" color="success.main" gutterBottom>
        Bets Placed Successfully!
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 3 }}>
        Your {totalCombinations} bets have been placed and will be processed for the next draws.
      </Typography>
      
      {betData && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Bet Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">Batch ID: BATCH-{Date.now()}</Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Number: {betData.number}<br/>
              Total Combinations: {totalCombinations}<br/>
              Total Amount: ${totalAmount.toFixed(2)}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">Dates:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 1 }}>
                {betData.dates.map((date) => (
                  <Chip key={date} label={new Date(date).toLocaleDateString()} size="small" />
                ))}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Counters:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 1 }}>
                {betData.counters.map((counter) => (
                  <Chip key={counter} label={counter} size="small" />
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
      
      <Button variant="contained" onClick={handleReset} sx={{ mr: 2 }}>
        Place Another Bet
      </Button>
      <Button variant="outlined" onClick={() => window.location.href = '/history'}>
        View History
      </Button>
    </Paper>
  );

  return (
    <Container maxWidth="md">
      <Typography variant="h4" gutterBottom>
        Place Bet
      </Typography>
      
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      
      {activeStep === 0 && renderEntryStep()}
      {activeStep === 1 && renderPreviewStep()}
      {activeStep === 2 && renderAcknowledgementStep()}
      
      {activeStep < 2 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canAfford && totalAmount > 0}
          >
            {activeStep === 1 ? 'Confirm Bet' : 'Next'}
          </Button>
        </Box>
      )}
      
      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>Confirm Bet Placement</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to place this bet? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleConfirmBet} 
            variant="contained" 
            disabled={placeBetMutation.isPending}
          >
            {placeBetMutation.isPending ? 'Placing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PlaceBet;